import { useEffect } from "react";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Form, Button, Tooltip, OverlayTrigger } from "react-bootstrap";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import FileInput from "../common/file-input";
import { uploadFiles } from "./visualize-form.utils";
import { defaultParams, loadingState, formState, paramsSelector, resultsState, statusState } from "./visualize.state";

export default function VisualizeForm(props) {
  const { id } = useParams();
  const { handleSubmit, register, reset, watch, control } = useForm({
    mode: "onChange",
    defaultValues: defaultParams,
  });
  const params = useRecoilValue(paramsSelector(id));
  const [loading, setLoading] = useRecoilState(loadingState);
  const setFormState = useSetRecoilState(formState);
  const resetResults = useResetRecoilState(resultsState);
  const resetStatus = useResetRecoilState(statusState);
  const navigate = useNavigate();
  const useAsync = watch("useAsync");

  useEffect(() => reset(params), [reset, params]);
  useEffect(() => setFormState(params), [setFormState, params]);

  async function onSubmit(data) {
    try {

      setLoading(true);
      const id = uuidv4();
      const submitParams = {
        id,
        vcfFile: data.vcfFile[0].name,
        limit: +data.limit || 1000,
        gene: data.gene.replace(/ /g,""),
        useAsync: data.useAsync,
        email: data.email,
        jobName: data.jobName,
      };
      console.log(submitParams);
      const apiEndpoint = `${process.env.PUBLIC_URL}/api`;
      await uploadFiles(`${apiEndpoint}/upload/${id}`, data);
      
      setFormState(submitParams);
      resetResults();
      resetStatus();
      
      await axios.post(`${apiEndpoint}/submit/${id}`, submitParams);
      navigate(`/visualize/${id}`);
      if (props.onSubmit) {
        props.onSubmit(submitParams);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleReset() {
    reset(defaultParams);
    setFormState(defaultParams);
    if (id) navigate("/visualize");
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} onReset={handleReset} disabled={loading}>
      <Form.Group className="mb-3" controlId="vcfFile">
        <Form.Label className="required">VCF File</Form.Label>
        <FileInput control={control} rules={{ required: true }} name="vcfFile" required accept=".vcf,.gz" disabled={id} />

        <Form.Text>
          <a href={`${process.env.PUBLIC_URL}/files/example.vcf`} download>
            example.vcf
          </a>
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3" controlId="limit">
        <Form.Label>
          Variant Limit
          <OverlayTrigger overlay={<Tooltip># of variants to visualize is 2-10000</Tooltip>}>
            <i className="bi bi-info-circle ms-1 small" />
          </OverlayTrigger>
        </Form.Label>
        <Form.Control type="number" placeholder="Maximum # of Variants" min="2" max="10000" name="limit" disabled={id} {...register("limit", { min: 2, max: 10000 })} />
      </Form.Group>

      <Form.Group className="mb-3" controlId="gene">
        <Form.Label>Gene</Form.Label>
        <Form.Control name="gene" placeholder="Enter Gene Symbol(s)" disabled={id} {...register("gene")} />
      </Form.Group>

      <Form.Group className="mb-3" controlId="useAsync">
        <Form.Check
          type="checkbox"
          label={
            <>
              Long-running job
              <OverlayTrigger overlay={<Tooltip>Run the job in the background and receive a notification upon completion.</Tooltip>}>
                <i className="bi bi-info-circle ms-1 small" />
              </OverlayTrigger>
            </>
          }
          name="useAsync"
          disabled={id}
          {...register("useAsync")}
        />
      </Form.Group>

      <fieldset className={useAsync ? "d-block" : "d-none"}>
        <Form.Group className="mb-3" controlId="email">
          <Form.Label className={useAsync && "required"}>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Enter Email Address"
            required={useAsync}
            disabled={id || !useAsync}
            {...register("email", { required: useAsync, disabled: id || !useAsync })}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="jobName">
          <Form.Label className={useAsync && "required"}>Job Name</Form.Label>
          <Form.Control
            placeholder="Enter Job Name"
            required={useAsync}
            disabled={id || !useAsync}
            {...register("jobName", { required: useAsync, disabled: id || !useAsync })}
          />
        </Form.Group>
      </fieldset>

      <div className="text-end">
        <Button type="reset" variant="outline-danger" className="me-1">
          Reset
        </Button>
        <Button type="submit" variant="primary" disabled={id || loading}>
          Submit
        </Button>
      </div>
    </Form>
  );
}
